import * as React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Translate } from '../../shared/LanguageService';
import { FeatureFlag, FeatureFlagRule } from './types';

const columnCount = 8;

interface FeatureFlagsTableProps {
    flags: FeatureFlag[];
    onEdit: (flag: FeatureFlag) => void;
    onDelete: (flag: FeatureFlag) => void;
}

function formatDate(iso: string): string {
    try {
        const d = new Date(iso);
        return d.toLocaleString();
    } catch {
        return iso;
    }
}

export default function FeatureFlagsTable({ flags, onEdit, onDelete }: FeatureFlagsTableProps) {
    const [expandedKey, setExpandedKey] = React.useState<string | null>(null);

    if (flags.length === 0) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center' }} variant="outlined">
                {Translate('feature-flags/table/empty')}
            </Paper>
        );
    }

    function toggleExpand(key: string) {
        setExpandedKey((current) => (current === key ? null : key));
    }

    return (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 48 }} />
                        <TableCell>{Translate('feature-flags/table/header/key')}</TableCell>
                        <TableCell>{Translate('feature-flags/table/header/description')}</TableCell>
                        <TableCell>{Translate('feature-flags/table/header/all-users')}</TableCell>
                        <TableCell align="right">{Translate('feature-flags/table/header/rules')}</TableCell>
                        <TableCell>{Translate('feature-flags/table/header/last-updated')}</TableCell>
                        <TableCell>{Translate('feature-flags/table/header/updated-by')}</TableCell>
                        <TableCell align="right">{Translate('feature-flags/table/header/actions')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {flags.map((flag) => {
                        const isExpanded = expandedKey === flag.key;
                        const hasRules = flag.rules.length > 0;
                        return (
                            <React.Fragment key={flag.key}>
                                <TableRow hover>
                                    <TableCell sx={{ borderBottom: isExpanded ? 'none' : undefined }}>
                                        <IconButton
                                            size="small"
                                            aria-label={
                                                isExpanded
                                                    ? Translate('feature-flags/table/collapse-button')
                                                    : Translate('feature-flags/table/expand-button')
                                            }
                                            onClick={() => toggleExpand(flag.key)}
                                            disabled={!hasRules}
                                        >
                                            {isExpanded ? (
                                                <KeyboardArrowUpIcon fontSize="small" />
                                            ) : (
                                                <KeyboardArrowDownIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: isExpanded ? 'none' : undefined }}>
                                        <code>{flag.key}</code>
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: isExpanded ? 'none' : undefined }}>
                                        {flag.description != null ? flag.description : ''}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: isExpanded ? 'none' : undefined }}>
                                        {flag.variants.length > 0 ? (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={flag.defaultVariant}
                                            />
                                        ) : (
                                            <Chip
                                                size="small"
                                                color={flag.defaultEnabled ? 'success' : 'default'}
                                                label={
                                                    flag.defaultEnabled
                                                        ? Translate('feature-flags/common/on')
                                                        : Translate('feature-flags/common/off')
                                                }
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ borderBottom: isExpanded ? 'none' : undefined }}
                                    >
                                        {flag.rules.length}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: isExpanded ? 'none' : undefined }}>
                                        {formatDate(flag.updatedAt)}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: isExpanded ? 'none' : undefined }}>
                                        {flag.updatedBy}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ borderBottom: isExpanded ? 'none' : undefined }}
                                    >
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            <Tooltip title={Translate('feature-flags/table/edit-button')}>
                                                <IconButton
                                                    size="small"
                                                    aria-label={Translate('feature-flags/table/edit-flag-aria', {
                                                        flagKey: flag.key
                                                    })}
                                                    onClick={() => onEdit(flag)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={Translate('feature-flags/table/delete-button')}>
                                                <IconButton
                                                    size="small"
                                                    aria-label={Translate('feature-flags/table/delete-flag-aria', {
                                                        flagKey: flag.key
                                                    })}
                                                    color="error"
                                                    onClick={() => onDelete(flag)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={columnCount} sx={{ p: 0, borderBottom: 0 }}>
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <Box sx={{ px: 2, py: 2, bgcolor: 'action.hover' }}>
                                                <RulesPreview rules={flag.rules} />
                                            </Box>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function RulesPreview({ rules }: { rules: FeatureFlagRule[] }) {
    if (rules.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {Translate('feature-flags/table/rules/empty')}
            </Typography>
        );
    }

    const sorted = rules.slice().sort((a, b) => a.priority - b.priority);

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={{ width: 80 }}>{Translate('feature-flags/table/rules/header/priority')}</TableCell>
                    <TableCell sx={{ width: 140 }}>{Translate('feature-flags/table/rules/header/type')}</TableCell>
                    <TableCell sx={{ width: 100 }}>{Translate('feature-flags/table/rules/header/operator')}</TableCell>
                    <TableCell>{Translate('feature-flags/table/rules/header/values')}</TableCell>
                    <TableCell sx={{ width: 100 }}>{Translate('feature-flags/table/rules/header/result')}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {sorted.map((rule) => (
                    <TableRow key={rule.id}>
                        <TableCell>{rule.priority}</TableCell>
                        <TableCell>{rule.ruleType}</TableCell>
                        <TableCell>{rule.operator}</TableCell>
                        <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {rule.values.map((value, index) => (
                                    <Chip
                                        key={`${rule.id}-${index}`}
                                        size="small"
                                        label={value}
                                        variant="outlined"
                                    />
                                ))}
                            </Stack>
                        </TableCell>
                        <TableCell>
                            {rule.resultVariant !== null ? (
                                <Chip size="small" variant="outlined" label={rule.resultVariant} />
                            ) : (
                                <Chip
                                    size="small"
                                    color={rule.resultEnabled ? 'success' : 'default'}
                                    label={
                                        rule.resultEnabled
                                            ? Translate('feature-flags/common/on')
                                            : Translate('feature-flags/common/off')
                                    }
                                />
                            )}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}
